import React, { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { Modal, Button, Form } from "react-bootstrap";

/**
 * SampleModel
 * - Collect Sample modal (test selection + collected) + barcode printing.
 * - Manual "Barcode Copies" override (0/empty = auto = number of selected non-radiology tests).
 * - Print layout comes from the `config` prop (backend-loaded via Barcode Configuration tab).
 * - Label order (top -> bottom): Patient Name -> Barcode -> UHID -> Age / Gender
 */

const FALLBACK_CONFIG = {
  columns: 2,
  paperWidth: 80,
  labelWidth: 36,
  labelHeight: 0,
  rowGap: 3,
  columnGap: 3,
  barWidth: 2,
  barHeight: 40,
  fontSize: 14,
  showBorder: true,
  outerBorder: false,
  leftMargin: 0,
  topMargin: 0,
};

// ✅ Radiology = department cat_id 60
// ✅ Radiology = test_cat 60
const isRadiologyTest = (t) => String(t?.test_cat ?? '').trim() === '60';

// ✅ Shared builder: each ROW of labels prints on its own page (one label pitch).
// Layout order: Name -> Barcode -> UHID -> Age/Gender (each on its own line).
export const buildLabelSheetHtml = ({ name = "", uhid = "", ageGender = "", count = 1, cfg = {} }) => {
  const c = { ...FALLBACK_CONFIG, ...(cfg || {}) };
  const cols     = parseInt(c.columns) === 1 ? 1 : 2;
  const colGap   = cols === 2 ? (Number(c.columnGap) || 0) : 0;
  const labelW   = Number(c.labelWidth) || 36;
  const labelH   = Number(c.labelHeight) || 0;
  const paperW   = Number(c.paperWidth) || 80;
  const barWidth = Number(c.barWidth) || 2;
  const barHeight= Number(c.barHeight) || 40;
  const fontSize = Number(c.fontSize) || 14;
  const border   = c.showBorder ? "1px solid #000" : "none";
  const leftMargin = Number(c.leftMargin) || 0;
  const topMargin  = Number(c.topMargin) || 0;

  const esc = (s) => String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#039;");

  const total = Math.max(1, parseInt(count) || 1);
  const rows = Math.ceil(total / cols);

  let rowsHtml = "";
  let idx = 0;
  for (let r = 0; r < rows; r++) {
    let cells = "";
    for (let col = 0; col < cols && idx < total; col++, idx++) {
      cells += `
        <div class="label">
          <div class="name">${esc(name)}</div>
          <div class="svg-wrap" data-payload="${esc(uhid)}" data-width="${barWidth}" data-height="${barHeight}"></div>
          <div class="uhid">${esc(uhid)}</div>
          <div class="agender">${esc(ageGender)}</div>
        </div>`;
    }
    rowsHtml += `<div class="label-row${r > 0 ? ' brk' : ''}">${cells}</div>`;
  }

  const pageHeight = labelH ? `${labelH}mm` : "auto";

  return `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Print Barcodes</title>
<style>
  @page { size: ${paperW}mm ${pageHeight}; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin:0; padding:0; }
  body { font-family: Arial, Helvetica, sans-serif; background:#fff; }
  .label-row {
    width:${paperW}mm;
    ${labelH ? `height:${labelH}mm;` : ''}
    display:flex;
    column-gap:${colGap}mm;
    justify-content:flex-start;
    padding-left:${leftMargin}mm;
    padding-top:${topMargin}mm;
    overflow:hidden;
  }
  .label-row.brk { page-break-before: always; break-before: page; }
  .label {
    width:${labelW}mm;
    ${labelH ? `height:${labelH}mm;` : ''}
    border:${border};
    padding:1mm 1.5mm;
    text-align:center;
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    overflow:hidden;
    line-height:1.15;
  }
  .label .name {
    font-size:${Math.max(9, fontSize)}px;
    font-weight:700;
    text-transform:uppercase;
    width:100%;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
    margin-bottom:0.6mm;
  }
  .label .svg-wrap { width:100%; display:flex; justify-content:center; align-items:center; }
  .label svg { width:100%; height:auto; display:block; }
  .label .uhid {
    font-size:${fontSize}px;
    font-weight:700;
    letter-spacing:0.5px;
    width:100%;
    white-space:nowrap;
    margin-top:0.4mm;
  }
  .label .agender {
    font-size:${Math.max(8, fontSize - 1)}px;
    font-weight:600;
    width:100%;
    white-space:nowrap;
  }
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
</head>
<body>
  ${rowsHtml}
  <script>
    (function(){
      document.querySelectorAll('.svg-wrap').forEach(function(el){
        var payload = el.getAttribute('data-payload') || '';
        var w = Number(el.getAttribute('data-width')) || 2;
        var h = Number(el.getAttribute('data-height')) || 40;
        var svg = document.createElementNS("http://www.w3.org/2000/svg","svg");
        el.appendChild(svg);
        try { JsBarcode(svg, payload, { format:"CODE128", displayValue:false, width:w, height:h, margin:0 }); }
        catch(e){ console.error('Barcode render failed', e); }
      });
      setTimeout(function(){ window.print(); }, 350);
    })();
  </script>
</body>
</html>`;
};

export const openPrintWindow = (html) => {
  const w = window.open("", "_blank");
  if (!w) { alert("Popup blocked. Please allow popups for this site."); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
};

const SampleModel = ({ item, onUpNow, onClose, show, config }) => {
  const cfg = { ...FALLBACK_CONFIG, ...(config || {}) };

  const tests = Array.isArray(item?.params) ? item.params : (item?.all_tests || []);
  const [selectedTests, setSelectedTests] = useState([]);
  const [sampleType, setSampleType] = useState("");
  const [sampleQty, setSampleQty] = useState(1);
  const [contType, setContType] = useState("");
  const [sampleCndi, setSampleCndi] = useState("");
  const [copies, setCopies] = useState(0);

  const previewRef = useRef(null);

  useEffect(() => {
    if (tests && tests.length) {
      setSelectedTests(tests.filter(t => !isRadiologyTest(t)));
    } else {
      setSelectedTests([]);
    }
    setSampleType("");
    setSampleQty(1);
    setContType("");
    setSampleCndi("");
    setCopies(0);
  }, [item]);

  const toggleTestSelection = (test) => {
    const idOf = (t) => (t.lab_item_test_id || t.lab_item_test_name || t.test_id || t.test_name);
    setSelectedTests(prev => {
      const found = prev.some(t => idOf(t) === idOf(test));
      if (found) return prev.filter(t => idOf(t) !== idOf(test));
      return [...prev, test];
    });
  };

  const autoCount = () => (selectedTests && selectedTests.length ? selectedTests.length : 1);
  const effectiveCount = () => {
    const c = parseInt(copies) || 0;
    return c > 0 ? c : autoCount();
  };

  const patientName = (item?.lab_reg_name || "").toString();
  const uhId = (item?.lab_reg_uhid || "").toString();
  const ageGender = `${item?.lab_reg_age_year || 0}Y / ${item?.lab_reg_gender || ''}`;

  // ---- In-modal live preview: matches the print (name -> barcode -> uhid -> age/gender) ----
  useEffect(() => {
    if (!previewRef.current) return;
    previewRef.current.innerHTML = "";
    try {
      const cols = parseInt(cfg.columns) === 1 ? 1 : 2;
      const count = Math.min(effectiveCount(), cols === 2 ? 4 : 2);
      const fs = Number(cfg.fontSize) || 14;

      const container = document.createElement("div");
      container.style.display = "grid";
      container.style.gridTemplateColumns = `repeat(${cols}, ${Number(cfg.labelWidth) || 36}mm)`;
      container.style.columnGap = `${cols === 2 ? (Number(cfg.columnGap) || 0) : 0}mm`;
      container.style.rowGap = `${Number(cfg.rowGap) || 0}mm`;
      container.style.justifyContent = "start";
      container.style.background = "#fff";
      container.style.padding = "2mm";
      if (cfg.outerBorder) container.style.border = "1px solid #000";

      for (let i = 0; i < count; i++) {
        const wrapper = document.createElement("div");
        wrapper.style.width = `${Number(cfg.labelWidth) || 36}mm`;
        if (cfg.labelHeight) wrapper.style.height = `${cfg.labelHeight}mm`;
        wrapper.style.border = cfg.showBorder ? "1px solid #000" : "1px solid #eee";
        wrapper.style.boxSizing = "border-box";
        wrapper.style.padding = "1mm 1.5mm";
        wrapper.style.textAlign = "center";
        wrapper.style.display = "flex";
        wrapper.style.flexDirection = "column";
        wrapper.style.alignItems = "center";
        wrapper.style.justifyContent = "center";
        wrapper.style.overflow = "hidden";
        wrapper.style.lineHeight = "1.15";

        const nameDiv = document.createElement("div");
        nameDiv.textContent = patientName || "PATIENT NAME";
        nameDiv.style.fontSize = `${Math.max(9, fs)}px`;
        nameDiv.style.fontWeight = "700";
        nameDiv.style.textTransform = "uppercase";
        nameDiv.style.whiteSpace = "nowrap";
        nameDiv.style.marginBottom = "0.6mm";
        wrapper.appendChild(nameDiv);

        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.style.display = "block";
        svg.style.width = "100%";
        svg.style.height = "auto";
        wrapper.appendChild(svg);
        JsBarcode(svg, uhId || "0000000000", {
          format: "CODE128",
          width: Number(cfg.barWidth) || 2,
          height: Number(cfg.barHeight) || 40,
          displayValue: false,
          margin: 0,
        });

        const uhidDiv = document.createElement("div");
        uhidDiv.textContent = uhId || "0000000000";
        uhidDiv.style.fontSize = `${fs}px`;
        uhidDiv.style.fontWeight = "700";
        uhidDiv.style.letterSpacing = "0.5px";
        uhidDiv.style.whiteSpace = "nowrap";
        uhidDiv.style.marginTop = "0.4mm";
        wrapper.appendChild(uhidDiv);

        const agDiv = document.createElement("div");
        agDiv.textContent = ageGender;
        agDiv.style.fontSize = `${Math.max(8, fs - 1)}px`;
        agDiv.style.fontWeight = "600";
        agDiv.style.whiteSpace = "nowrap";
        wrapper.appendChild(agDiv);

        container.appendChild(wrapper);
      }
      previewRef.current.appendChild(container);
    } catch (err) {
      console.error("Preview render error:", err);
    }
  }, [selectedTests, copies, item, config]);

  const handleConfirm = async () => {
    if (!item) return;
    await onUpNow(item.lab_reg_id, sampleType, sampleQty, contType, sampleCndi);
    if (onClose) onClose();
  };

  const handlePrintBarcode = () => {
    if (!uhId) {
      alert("UHID missing — cannot print barcode.");
      return;
    }
    const html = buildLabelSheetHtml({
      name: patientName,
      uhid: uhId,
      ageGender,
      count: effectiveCount(),
      cfg,
    });
    openPrintWindow(html);
  };

  return (
    <Modal show={!!item || !!show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Collect Sample - {item?.lab_reg_name || "Patient"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <strong>UHID:</strong> {item?.lab_reg_uhid} &nbsp;
          <strong>Reg No:</strong> {item?.lab_reg_id} &nbsp;
          <strong>Age/Gender:</strong> {ageGender}
        </div>
        <hr />

        <div>
          <h6>Tests (default: all non-radiology tests selected)</h6>
          {tests.length ? (
            <div style={{ maxHeight: 200, overflowY: "auto", paddingRight: 6 }}>
              {tests.map((t, i) => {
                const idKey = (t.lab_item_test_id || t.lab_item_test_name || `t-${i}`).toString();
                const isRadiology = isRadiologyTest(t);
                const idOf = (x) => (x.lab_item_test_id || x.lab_item_test_name || x.test_id || x.test_name);
                const checked = selectedTests.some(s => idOf(s) === idOf(t));

                return (
                  <Form.Check
                    key={idKey}
                    type="checkbox"
                    id={`test_chk_${idKey}`}
                    label={
                      <span style={{ color: isRadiology ? '#999' : 'inherit' }}>
                        {t.lab_item_test_name || t.test_name || "Unnamed Test"}
                        {isRadiology && (
                          <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '10px' }}>
                            Radiology - No Sample
                          </span>
                        )}
                      </span>
                    }
                    checked={checked}
                    onChange={() => !isRadiology && toggleTestSelection(t)}
                    disabled={isRadiology}
                    style={{ opacity: isRadiology ? 0.6 : 1 }}
                  />
                );
              })}
            </div>
          ) : <div>No tests available</div>}
        </div>

        <hr />

        <div className="p-3 border rounded" style={{ background: '#fff8e1', borderColor: '#ffe082' }}>
          <label className="form-label fw-bold mb-2" style={{ color: '#e65100' }}>
            <i className="ri-file-copy-line me-2"></i>Number of Barcode Copies
          </label>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <input
              type="number"
              className="form-control"
              min="0"
              placeholder={`Auto (${autoCount()})`}
              value={copies || ''}
              onChange={(e) => {
                const v = e.target.value;
                setCopies(v === '' ? 0 : Math.max(0, parseInt(v) || 0));
              }}
              style={{ maxWidth: 140 }}
            />
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCopies(0)} title="Reset to auto">
              <i className="ri-refresh-line me-1"></i>Auto
            </button>
            <small className="text-muted">
              Leave empty or 0 for auto test-wise count ({autoCount()}). Will print:&nbsp;
              <strong>{effectiveCount()}</strong>
              {parseInt(copies) > 0
                ? <span className="badge bg-warning text-dark ms-2">Custom</span>
                : <span className="badge bg-success ms-2">Auto</span>}
            </small>
          </div>
          <small className="text-muted d-block mt-2">
            Layout: <strong>{parseInt(cfg.columns) === 1 ? '1 Column' : '2 Columns'}</strong>
            {' · '}{cfg.labelWidth}mm{cfg.labelHeight ? ` × ${cfg.labelHeight}mm` : ''} — set in the Barcode Configuration tab (backend).
          </small>
        </div>

        <hr />

        <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: 'wrap' }}>
          <div ref={previewRef} aria-hidden="true" style={{ flex: 1, minWidth: 240, background: "#f9f9f9", padding: 8, borderRadius: 4, overflowX: 'auto' }} />
          <div style={{ minWidth: 220 }}>
            <div><strong>Barcode Preview</strong></div>
            <div style={{ marginTop: 8 }}>
              <Button variant="secondary" size="sm" onClick={handlePrintBarcode}>
                <i className="ri-printer-line me-1"></i>Print Barcode
              </Button>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}></div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleConfirm}>Collected</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SampleModel;